import React, { useMemo, useState } from "react";
import { Box, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useDraftData } from "../context/DraftDataContext";
import { useMarketAdp } from "../helpers/useMarketAdp";
import { buildWrappedMetrics } from "../helpers/wrappedMetrics";
import { isWrappedUnlocked } from "../config/season";
import { BRAND } from "../config/theme";
import WrappedStory from "../components/wrapped/WrappedStory";
import WrappedCountdown from "../components/wrapped/WrappedCountdown";

const C = { text: "#f2f3f5", dim: "#a8afba", green: BRAND.green, greenOnDark: BRAND.greenOnDark, hairline: "rgba(255,255,255,.10)" };

/** Full-screen empty state — sync/upload prompt when there's no data yet. */
function WrappedEmpty({ onClose }: { onClose: () => void }) {
  return (
    <Box sx={{
      position: "fixed", inset: 0, zIndex: 2000,
      fontFamily: "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      color: C.text, display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", textAlign: "center", p: "40px 30px",
      background: `radial-gradient(120% 70% at 50% 6%, #24421d 0%, rgba(36,66,29,0) 46%), linear-gradient(180deg, #14280f 0%, #070f08 100%)`,
    }}>
      <Box component="button" aria-label="Close" onClick={onClose} sx={{ position: "absolute", top: 24, right: 20, width: 32, height: 32, borderRadius: "50%", background: "rgba(0,0,0,.3)", border: `1px solid ${C.hairline}`, color: "#eaf5e6", cursor: "pointer", fontSize: 16 }}>×</Box>
      <Typography sx={{ fontSize: 24, fontWeight: 800, letterSpacing: "-.01em", maxWidth: "18ch" }}>Sync your drafts to unlock your Wrapped.</Typography>
      <Typography sx={{ color: C.dim, fontSize: 14, mt: 1.5, maxWidth: "34ch" }}>
        Import your Underdog and DraftKings drafts from the rosterOS extension, or upload your CSVs, then come back here.
      </Typography>
      <Box component="button" onClick={onClose} sx={{ mt: 3.5, fontSize: 14, fontWeight: 700, borderRadius: "12px", p: "12px 22px", cursor: "pointer", background: "#eaf5e6", color: "#12240f", border: "1px solid transparent" }}>
        Go to dashboard
      </Box>
    </Box>
  );
}

export default function WrappedPage() {
  const navigate = useNavigate();
  const { rawData, portfolioStacks } = useDraftData();
  const { lookupForProvider } = useMarketAdp();
  // Re-check the lock once, plus a manual flip when the countdown crosses zero.
  const [unlocked, setUnlocked] = useState(() => isWrappedUnlocked());

  const metrics = useMemo(
    () => buildWrappedMetrics({ rawData, lookupForProvider, portfolioStacks }),
    [rawData, lookupForProvider, portfolioStacks]
  );

  const close = () => navigate("/dashboard");

  // The story/countdown/empty are all position:fixed (out of flow). Embedded in
  // the rosteros.com iframe, the parent sizes the iframe from our reported
  // scrollHeight — so with no in-flow content it would collapse and the fixed
  // overlay would clip. This in-flow sizer gives the iframe an explicit height to
  // fill; standalone (not embedded) the fixed overlay covers the real viewport.
  const embedded = typeof window !== "undefined" && window.self !== window.top;
  const stageHeight = embedded ? 880 : "100vh";

  let content: React.ReactNode;
  if (!unlocked) content = <WrappedCountdown onClose={close} onUnlock={() => setUnlocked(true)} />;
  else if (!metrics.hasData) content = <WrappedEmpty onClose={close} />;
  else content = <WrappedStory metrics={metrics} onClose={close} />;

  return (
    <>
      <Box aria-hidden sx={{ height: stageHeight }} />
      {content}
    </>
  );
}
